import React, { useState, useEffect, useContext, useMemo } from "react";
import { Container, Spinner } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import styles from "../../AmbulatoryVisit/css/ambulatoryVisit.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { BreadcrumbContext } from "Layouts/Header";
import { formatDate, showToastMessage } from "common/service/CommonService";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "Components/Common/Commonbadge";
import CommonButton from "Components/Common/CommonButton";
import { validations } from "validations/FormValidations";
import FullPageLoader from "Components/Common/FullPageLoader";
import {
  getPhysicalExamFields,
  getLaboratoryExamFields,
} from "../helper/poststudyassessmentconstants";
import PostStudyAssessmentStepper from "../components/PostStudyAssessmentStepper";
import { usePostStudyAssessmentHook } from "../api/usePostStudyAssessmentHook";

const AddPostStudyAssessmentSubjectDetail: React.FC = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams<{ subjectId: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);
  const subjectno = subjectId || "-";

  const { InsertVitalSignsMutation, UpdateVitalSignsMutation, InsertLaboratoryAssessmentMutation, SelectAssessmentQuery } = usePostStudyAssessmentHook({ studyid, subjectno });
  const { data: assessmentData, isLoading: isLoadingAssessment, refetch } = SelectAssessmentQuery;
  const existingData = useMemo(() => assessmentData?.data, [assessmentData]);

  const [poststudyassessmentid, setPoststudyassessmentid] = useState<number | null>(null);
  const [isSubmittingVitals, setIsSubmittingVitals] = useState(false);
  const [isSubmittingLab, setIsSubmittingLab] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [vitalsSaved, setVitalsSaved] = useState(false);
  const [labSaved, setLabSaved] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [activeTab, setActiveTab] = useState<"vital_signs" | "laboratory">("vital_signs");

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingPage(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Post Study Assessment", link: "/study/detail/poststudyassessment" },
      { text: `Subject ${subjectId || ""} Assessment` },
    ]);

    return () => {
      breadcrumbContext?.setBreadcrumbItems?.([]);
    };
  }, [subjectId]);

  // Initialize useForm hook for CommonForm (No pre-filled values, keeping fields clean with placeholders)
  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      position: "",
      actualTime: "",
      bloodPressure: "",
      pulseRate: "",
      respirationRate: "",
      oralTemp: "",
      vitalWellBeing: "",

      respiratorySystem: "",
      gastroIntestinalSystem: "",
      cardiovascularSystem: "",
      centralNervousSystem: "",
      wellBeingAssessment: "",
      participantStatus: "",

      remarks: "",

      // Laboratory Assessment default values
      bloodSamplesCollected: "",
      bloodVolumeCollected: "",
      clinicallySignificantAbnormal: "",
      abnormalDetails: "",
    },
    mode: "onChange",
  });

  const clinicallySignificantAbnormal = watch("clinicallySignificantAbnormal");

  useEffect(() => {
    if (existingData) {
      setPoststudyassessmentid(existingData.poststudyassessmentid);

      const formatRadioValue = (val?: string) => {
        if (!val) return "";
        const trimmed = val.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
      };

      setValue("position", formatRadioValue(existingData.position));
      if (existingData.actualtime) {
        setValue("actualTime", String(existingData.actualtime).substring(0, 5));
      }
      setValue("bloodPressure", existingData.bloodpressure || "");
      setValue("pulseRate", existingData.pulserate || "");
      setValue("respirationRate", existingData.respirationrate || "");
      setValue("oralTemp", existingData.oraltemperature || "");
      setValue("vitalWellBeing", formatRadioValue(existingData.wellbeingassessment));
      setValue("respiratorySystem", formatRadioValue(existingData.respiratorysystem));
      setValue("gastroIntestinalSystem", formatRadioValue(existingData.gastrointestinalsystem));
      setValue("cardiovascularSystem", formatRadioValue(existingData.cardiovascularsystem));
      setValue("centralNervousSystem", formatRadioValue(existingData.centralnervoussystem));
      setValue("participantStatus", formatRadioValue(existingData.participantstatus));
      setValue("remarks", existingData.remarks || "");

      // Laboratory Assessment Data Binding
      const formatBoolToYesNo = (val: boolean | null | undefined) => {
        if (val === true) return "Yes";
        if (val === false) return "No";
        return "";
      };

      if (existingData.bloodsamplecollected !== null && existingData.bloodsamplecollected !== undefined) {
        setValue("bloodSamplesCollected", formatBoolToYesNo(existingData.bloodsamplecollected));
        setValue("bloodVolumeCollected", existingData.bloodvolume || "");
        setValue("clinicallySignificantAbnormal", formatBoolToYesNo(existingData.abnormalvalues));
        setValue("abnormalDetails", existingData.abnormalvaluesdetail || "");
      }

      if (existingData.physicalexamstatus === "completed") {
        setVitalsSaved(true);
      }
      
      if (existingData.labassessmentstatus === "completed") {
        setLabSaved(true);
      }
    }
  }, [existingData, setValue]);

  // Physical Examination / Vital Signs Fields Config (Imported from helper)
  const physicalExamFields = useMemo(() => getPhysicalExamFields(), []);

  // Laboratory Assessment Fields Config (Imported from helper, reactive on abnormal flag)
  const laboratoryExamFields = useMemo(
    () => getLaboratoryExamFields(clinicallySignificantAbnormal),
    [clinicallySignificantAbnormal]
  );

  // Separate save handler for Vital Signs Measurement card
  const handleSaveVitals = async () => {
    const fieldsToValidate = [
      "position",
      "actualTime",
      "bloodPressure",
      "pulseRate",
      "respirationRate",
      "oralTemp",
      "vitalWellBeing",
      "respiratorySystem",
      "gastroIntestinalSystem",
      "cardiovascularSystem",
      "centralNervousSystem",
      "participantStatus",
    ];

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    const data = getValues();
    const payload = {
      studyid: Number(sessionStorage.getItem("activestudyid")),
      subjectno: subjectId,
      assessmentdate: new Date().toISOString(),
      position: data.position,
      actualtime: data.actualTime && data.actualTime.length === 5 ? data.actualTime + ":00" : data.actualTime,
      bloodpressure: data.bloodPressure,
      pulserate: Number(data.pulseRate),
      respirationrate: Number(data.respirationRate),
      oraltemperature: Number(data.oralTemp),
      wellbeingassessment: data.vitalWellBeing,
      respiratorysystem: data.respiratorySystem,
      gastrointestinalsystem: data.gastroIntestinalSystem,
      cardiovascularsystem: data.cardiovascularSystem,
      centralnervoussystem: data.centralNervousSystem,
      participantstatus: data.participantStatus,
      remarks: data.remarks || null,
    };

    // Insert
    setIsSubmittingVitals(true);
    InsertVitalSignsMutation.mutate(payload, {
      onSuccess: (res: any) => {
        setIsSubmittingVitals(false);
        setVitalsSaved(true);
        if (res?.data?.poststudyassessmentid) {
           setPoststudyassessmentid(res.data.poststudyassessmentid);
        } else if (typeof res?.data === "number") {
           setPoststudyassessmentid(res.data);
        }
        refetch();
        showToastMessage("success", res?.message || "Vital Signs Measurement saved successfully!");
        setActiveTab("laboratory");
      },
      onError: (err: any) => {
        setIsSubmittingVitals(false);
        showToastMessage("error", err?.message || "Failed to save Vital Signs.");
      }
    });
  };


  // Final Submit Handler
  const onSubmitFinalForm = async (data: any) => {
    // 1. Validate the laboratory fields first
    const fieldsToValidate = [
      "bloodSamplesCollected",
      "clinicallySignificantAbnormal",
      ...(clinicallySignificantAbnormal === "Yes" ? ["abnormalDetails"] : []),
    ];

    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    if (!poststudyassessmentid) {
      showToastMessage("error", "Please save Vital Signs Measurement first!");
      return;
    }

    // 2. Prepare payload
    const payload = {
      poststudyassessmentid: poststudyassessmentid,
      bloodsamplecollected: data.bloodSamplesCollected === "Yes",
      bloodvolume: data.bloodSamplesCollected === "Yes" ? Number(data.bloodVolumeCollected) : null,
      abnormalvalues: data.clinicallySignificantAbnormal === "Yes",
      abnormalvaluesdetail: data.clinicallySignificantAbnormal === "Yes" ? data.abnormalDetails : null,
    };

    setIsSubmittingFinal(true);
    InsertLaboratoryAssessmentMutation.mutate(payload, {
      onSuccess: (res: any) => {
        setIsSubmittingFinal(false);
        setLabSaved(true);
        showToastMessage("success", res?.message || "Post Study Assessment submitted successfully!");
        navigate("/study/detail/poststudyassessment");
      },
      onError: (err: any) => {
        setIsSubmittingFinal(false);
        showToastMessage("error", err?.message || "Failed to submit Laboratory Assessment.");
      }
    });
  };

  const isSubmitting = isSubmittingVitals || isSubmittingLab || isSubmittingFinal;

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      {(isLoadingPage || isSubmitting || isLoadingAssessment) && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 99999 }}>
          <FullPageLoader
            message={
              isSubmittingFinal
                ? "Submitting Post Study Assessment..."
                : isSubmittingVitals
                  ? "Saving Vital Signs Measurement..."
                  : isSubmittingLab
                    ? "Saving Laboratory Assessment..."
                    : isLoadingAssessment
                      ? "Loading Existing Assessment..."
                      : "Loading Subject Assessment..."
            }
          />
        </div>
      )}
      <Container fluid>

        <PostStudyAssessmentStepper
          activeTab={activeTab}
          isVitalsSaved={vitalsSaved}
          onStepClick={(tab) => {
            if (tab === "laboratory" && !vitalsSaved) {
              return;
            }
            setActiveTab(tab);
          }}
        />

        {/* 1. Subject Identification Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and post study assessment status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        {subjectId || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Assessment Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {formatDate(new Date())}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Status</div>
                    <Commonbadge status="checked in" style={{ width: "90px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 2. Vital Signs Measurement Card */}
        {activeTab === "vital_signs" && (
          <SectionCard
            icon="ri-pulse-line"
            title="Vital Signs Measurement"
            subtitle="Physical examination, vital signs, system assessments, and participant status."
            rightContent={
              <Commonbadge
                status={vitalsSaved ? "completed" : "pending"}
                style={{ fontSize: "12px" }}
              />
            }
          >
            <CommonForm
              fields={physicalExamFields}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
              getValues={getValues}
            />
          </SectionCard>
        )}

        {/* 3. Laboratory Assessment Card */}
        {activeTab === "laboratory" && (
          <SectionCard
            icon="ri-flask-line"
            title="Laboratory Assessment"
            subtitle="Record blood sample collection details and laboratory assessment findings."
            rightContent={
              <Commonbadge
                status={labSaved ? "completed" : "pending"}
                style={{ fontSize: "12px" }}
              />
            }
          >
            <CommonForm
              fields={laboratoryExamFields}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
              getValues={getValues}
            />
          </SectionCard>
        )}

      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
          <div className={formStyles.footerActions}>
            <div className="d-flex justify-content-between w-100">
              <CommonButton
                type="button"
                color="dark"
                outline
                onClick={() => navigate("/study/detail/poststudyassessment")}
              >
                Cancel
              </CommonButton>

              <div className="d-flex gap-2">
                {activeTab === "vital_signs" && (
                  <>
                    <CommonButton
                      type="button"
                      color="primary"
                      outline
                      disabled={isSubmittingVitals || vitalsSaved}
                      onClick={handleSaveVitals}
                    >
                      {isSubmittingVitals ? (
                        <><Spinner size="sm" className="me-2" /> Saving...</>
                      ) : (
                        "Save"
                      )}
                    </CommonButton>
                    <CommonButton
                      type="button"
                      color="primary"
                      disabled={!vitalsSaved}
                      onClick={() => setActiveTab("laboratory")}
                    >
                      Next
                    </CommonButton>
                  </>
                )}

                {activeTab === "laboratory" && (
                  <>
                    <CommonButton
                      type="button"
                      color="primary"
                      outline
                      onClick={() => setActiveTab("vital_signs")}
                    >
                      Back
                    </CommonButton>
                    <CommonButton
                      color="primary"
                      disabled={isSubmittingFinal}
                      onClick={handleSubmit(onSubmitFinalForm)}
                      className="fw-600"
                    >
                      {isSubmittingFinal ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </CommonButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPostStudyAssessmentSubjectDetail;
